import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronDown, LogOut, Search } from "lucide-react";
import { useAuth } from "./AuthContext";

const initials = (text) =>
  (text || "?")
    .replace(/[«»"'()]/g, "")
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");

const formatRub = (v) =>
  `${new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 2 }).format(v || 0)} ₽`;

const UserMenu = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!user) return null;
  const name = user.company?.name || user.email;

  return (
    <div className="px-user" ref={ref}>
      <button
        type="button"
        className="px-user-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="px-avatar" aria-hidden="true">
          {initials(name)}
        </span>
        <span className="px-user-name">{name}</span>
        <ChevronDown size={16} strokeWidth={2.5} aria-hidden="true" />
      </button>
      {open && (
        <div className="px-user-menu" role="menu">
          <div className="px-user-menu-head">
            <div className="px-user-menu-email">{user.email}</div>
            <div className="px-user-menu-meta">
              {user.is_admin
                ? "Администратор"
                : `Баланс ${formatRub(user.balance)}`}
            </div>
          </div>
          <button
            type="button"
            role="menuitem"
            className="px-menu-item"
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
          >
            <LogOut size={18} aria-hidden="true" /> Выйти
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * Шапка страницы внутри белой панели: заголовок, подзаголовок, действия
 * страницы и меню пользователя.
 */
const PageHeader = ({ title, subtitle, backTo, onBack, backLabel, leading, children }) => (
  <header className="px-page-header">
    {backTo && (
      <Link
        to={backTo}
        className="px-icon-btn"
        aria-label={backLabel || "Назад"}
      >
        <ArrowLeft size={22} strokeWidth={2.2} aria-hidden="true" />
      </Link>
    )}
    {onBack && (
      <button
        type="button"
        className="px-icon-btn"
        aria-label={backLabel || "Назад"}
        onClick={onBack}
      >
        <ArrowLeft size={22} strokeWidth={2.2} aria-hidden="true" />
      </button>
    )}
    {leading}
    <div className="px-page-heading">
      <h1 className="px-page-title">{title}</h1>
      {subtitle && <p className="px-page-sub">{subtitle}</p>}
    </div>
    {children}
    <Link to="/history" className="px-icon-btn" aria-label="Поиск в истории">
      <Search size={22} strokeWidth={2} aria-hidden="true" />
    </Link>
    <UserMenu />
  </header>
);

export { formatRub };
export default PageHeader;
